// 剣舞の床技「裾払い」(hemSweep・床の帯 kind 'veil') の見た目 — 板と刃の花 (2026-10-21・配布版の既定)。
//
// 作者の答え = 床技7つの見本の「A 刃の花」と「B 舞台の板」の組み合わせ (メモ「A,Bの組み合わせが良い」・
//   docs/research/2026-10-19-floor-fx-mihon.md §5・台帳 production.md 10-19 夜)。作者の物差し「名前通り作りたい」
//   = 踊り子が低く回って裾を払う → 舞台の床板が裾の払った順に跳ね上がり、板の隙間から裾の刃が花びらのように開いて立つ。
// 段:
//   ① 予告 (700ms): 帯の床に割れ目が走り、床の下から菫の照り。40% から割れ目の刃先が覗いて震え、最後の 25% は脈打つ。
//   ② 当たり 0ms〜: 踊り子の側から外へ、板が1枚ずつ跳ね上がり (約 24ms ずつずれる)、その隙間から刃の花びらが3枚ずつ開いて立つ。
//   ③ 当たりの終わり 100ms 前から: 花びらが砕けて刃のかけらが舞い、板は倒れて木くずが跳ねる。
//   ④ 当たりの後 約 0.42 秒: かけらが落ちて照りが引き、晴れる。
// 絵の部品 = Codex が1枚に描いた物を切り出した atlas (元絵 そざい/concept/blade-hem-1020/・
//   切り出しの記録は LUNAIRE-notes/assets/2026-10-20-blade-hem/)。動きはコード。
// 描くだけ。当たり (場所・時間・ダメージ) は変えない。絵は帯の ±V_BLEED の中だけ (刃先も帯の外へ倒さない)。
// 色: 菫は弾けない合図 (花嫁の影の手と同じ swc('unparry', …))。赤・白い線は使わない。金は板の縁の飾りだけ。
// 既定 (配布版) は板と刃の花。DEV の ?bladeHemFx=now で前の見た目 (花嫁の布の絵を借りた帯・render.ts) と比べる。
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'
import { sw as swc } from './purple_swap'

export type BladeHemFx = 'now' | 'bloom'
let hemFx: BladeHemFx = 'bloom'

/** main.ts が起動時に呼ぶ。'now' だけ前の見た目、それ以外は板と刃の花を先読みする。 */
export function setBladeHemFx(v: string | null): void {
  hemFx = v === 'now' ? 'now' : 'bloom'
  if (hemFx === 'bloom') preloadBladeHemAtlas()
}
export function getBladeHemFx(): BladeHemFx { return hemFx }

// ---------------------------------------------------------------- 部品の絵

type Rect = readonly [number, number, number, number]
// 名前: petal_i (刃の花びら・根元が下)・board_i (床板の表・縦長)・crack_i (床の割れ目・横長)・bshard_i (刃のかけら)・wood_i (木くず)。
export const HEM_ATLAS: Record<string, Rect> = {
  petal_0: [0, 0, 29, 170],
  petal_1: [31, 0, 28, 146],
  petal_2: [61, 0, 30, 154],
  petal_3: [93, 0, 38, 178],
  petal_4: [133, 0, 39, 155],
  petal_5: [174, 0, 27, 109],
  board_0: [203, 0, 41, 86],
  board_1: [246, 0, 41, 86],
  board_2: [289, 0, 44, 86],
  board_3: [335, 0, 42, 72],
  crack_0: [0, 180, 165, 28],
  crack_1: [167, 180, 121, 28],
  bshard_0: [290, 180, 18, 32],
  bshard_1: [310, 180, 10, 33],
  bshard_2: [322, 180, 18, 18],
  bshard_3: [342, 180, 31, 27],
  bshard_4: [375, 180, 24, 31],
  bshard_5: [401, 180, 15, 22],
  bshard_6: [418, 180, 16, 32],
  bshard_7: [436, 180, 15, 20],
  wood_0: [453, 180, 37, 32],
  wood_1: [492, 180, 20, 26],
  wood_2: [0, 215, 30, 27],
  wood_3: [32, 215, 26, 21],
  wood_4: [60, 215, 34, 24],
  wood_5: [96, 215, 24, 21],
}
const HEM_SRC = '/sprites/fx/hazard/blade_hem_atlas.png'
let hemImg: CanvasImageSource | null = null
let hemLoading = false
/** 部品の絵の読み込み (1回)。 */
export function preloadBladeHemAtlas(): void {
  if (hemImg || hemLoading || typeof Image === 'undefined') return
  hemLoading = true
  const img = new Image()
  img.onload = () => { hemImg = img }
  img.onerror = () => { hemLoading = false }
  img.src = HEM_SRC
}
/** テスト用 (偽の絵を差す)。 */
export function setBladeHemAtlasForTest(img: CanvasImageSource | null): void { hemImg = img }

const count = (prefix: string): number => Object.keys(HEM_ATLAS).filter((k) => k.startsWith(prefix + '_')).length

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
// 行き過ぎて戻る (最大 約1.1)
function easeOutBack(p: number): number {
  const x = clamp01(p) - 1
  const c = 1.7
  return 1 + (c + 1) * x * x * x + c * x * x
}

// 部品1つを、下端中央 (ax, ay) を基準に回して描く。dw×dh はゲームの px。
function part(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, name: string,
  ax: number, ay: number, dw: number, dh: number, rot: number, a: number, flip = false,
): void {
  if (a <= 0.01 || dw <= 0.5 || dh <= 0.5) return
  const r = HEM_ATLAS[name]
  if (!r) return
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(ax, ay)
  if (rot) ctx.rotate(rot)
  if (flip) ctx.scale(-1, 1)
  ctx.drawImage(img, r[0], r[1], r[2], r[3], -dw / 2, -dh, dw, dh)
  ctx.restore()
}

// ---------------------------------------------------------------- 帯ごとの配置

export const V_BLEED = 8      // 帯の左右へはみ出してよい幅
const WAVE_MS = 24            // 板が1枚ずつ跳ねるずれ (踊り子の側から外へ)
const BOARD_UP_MS = 70        // 板が跳ね上がる時間
const PETAL_UP_MS = 64        // 花びらが立つ時間
const BLOOM_MS = 110          // 両脇の花びらが開ききるまで
const SHATTER_LEAD_MS = 100   // 当たりの終わりの何 ms 前から砕けるか
export const LINGER_MS = 420  // 当たりの後、かけらが落ちて晴れるまで

type Petal = { name: string; h: number; rot: number; open: number; seed: number }
type Cluster = { x: number; delay: number; petals: Petal[]; seed: number }
type Board = { hinge: number; len: number; wid: number; name: string; delay: number; lift: number; seed: number }
type Geom = { xStart: number; xEnd: number; w: number; cx: number; dir: 1 | -1; seed: number; clusters: Cluster[]; boards: Board[] }

// 刃先が帯の ±V_BLEED の外へ出ない傾きに抑える。
function clampLean(x: number, h: number, rot: number, xStart: number, xEnd: number): number {
  const lo = xStart - V_BLEED, hi = xEnd + V_BLEED
  const room = rot > 0 ? hi - x : x - lo
  const maxSin = clamp01(room / h)
  const max = Math.asin(maxSin)
  return Math.sign(rot) * Math.min(Math.abs(rot), max)
}

function geomOf(hz: ActiveHit, bossX: number): Geom {
  const w = hz.xEnd - hz.xStart
  const cx = (hz.xStart + hz.xEnd) / 2
  const dir: 1 | -1 = cx >= bossX ? 1 : -1
  const seed = hz.id * 2.37 + 41.3
  const nPetal = Math.max(1, count('petal'))
  const nBoard = 3   // board_3 は折れた上半分 (折れ口が下) なので立つ板には使わない
  // 板の数は帯の幅から (裾払いの帯 130px＝4枚)。板は踊り子の側の端を蝶番に、外へ倒れた形から起き上がる。
  const n = Math.max(3, Math.min(6, Math.round(w / 32)))
  const len = Math.min(46, w * 0.35)   // 10-20 窓口: 一回り小さく (前は 64・0.48)
  const reach = len * Math.cos(0.5)    // 起き上がり始めの横の張り出し
  const span = Math.max(0, w - 8 - reach)
  const boards: Board[] = []
  const clusters: Cluster[] = []
  for (let i = 0; i < n; i += 1) {
    const s = seed + i * 11.3
    const u = n > 1 ? i / (n - 1) : 0.5
    // 踊り子の側の端から外へ並べる (dir=1 なら左端が踊り子の側)。
    const near = dir === 1 ? hz.xStart + 4 : hz.xEnd - 4
    const hinge = near + dir * (u * span + (hash01(s) - 0.5) * 4)
    boards.push({
      hinge, len: len * (0.86 + hash01(s + 1) * 0.2), wid: 18 + hash01(s + 2) * 5,
      name: `board_${(i + Math.floor(hash01(seed) * nBoard)) % nBoard}`,
      delay: i * WAVE_MS, lift: 1.0 + hash01(s + 3) * 0.22, seed: s,
    })
    // 刃の花: 板の蝶番の少し外側の隙間から、3枚 (真ん中が高い) が扇に開く。板が跳ねた後に、板の手前へ突き抜ける。
    const px = hinge + dir * (6 + hash01(s + 4) * 6)
    const petals: Petal[] = []
    const tallBase = 98 + hash01(s + 5) * 28
    for (let k = 0; k < 3; k += 1) {
      const side = k - 1
      const h = side === 0 ? tallBase : tallBase * (0.62 + hash01(s + 6 + k) * 0.14)
      const want = side * (0.32 + hash01(s + 9 + k) * 0.22)
      petals.push({
        name: `petal_${Math.floor(hash01(s + 12 + k) * nPetal) % nPetal}`,
        h, rot: clampLean(px, h, want, hz.xStart, hz.xEnd), open: side === 0 ? 0 : 1, seed: s + k * 3.1,
      })
    }
    clusters.push({ x: px, delay: i * WAVE_MS + 24, petals, seed: s })
  }
  return { xStart: hz.xStart, xEnd: hz.xEnd, w, cx, dir, seed, clusters, boards }
}

// ---------------------------------------------------------------- 描く

// 床の下からの菫の照り。帯の中だけ・横長の楕円 (四角い箱に見せない)。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, h: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.beginPath()
  ctx.rect(g.xStart - V_BLEED, floorY - h, g.w + V_BLEED * 2, h)
  ctx.clip()
  ctx.translate(g.cx, floorY)
  ctx.scale((g.w / 2 + V_BLEED) / h, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, h)
  gr.addColorStop(0, `rgba(${swc('unparry', '168,128,226')},${(0.46 * a).toFixed(3)})`)
  gr.addColorStop(0.6, `rgba(${swc('unparry', '150,110,204')},${(0.16 * a).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${swc('unparry', '150,110,204')},0)`)
  ctx.fillStyle = gr
  ctx.fillRect(-h, -h, h * 2, h)
  ctx.restore()
}

// 床の割れ目 (平たく・帯いっぱい)。open=0..1 で踊り子の側から外へ走る。
function drawCracks(ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, floorY: number, open: number, a: number): void {
  if (a <= 0.01 || open <= 0.01) return
  const name = `crack_${Math.floor(hash01(g.seed + 2) * Math.max(1, count('crack')))}`
  const r = HEM_ATLAS[name]
  if (!r) return
  const segW = (g.w + V_BLEED) * open
  const segH = Math.min(14, r[3] * (g.w / r[2]))
  const near = g.dir === 1 ? g.xStart - V_BLEED / 2 : g.xEnd + V_BLEED / 2
  ctx.save()
  ctx.beginPath()
  ctx.rect(Math.min(near, near + g.dir * segW), floorY - segH - 2, segW, segH + 10)
  ctx.clip()
  part(ctx, img, name, g.cx, floorY + segH * 0.45, g.w + V_BLEED, segH, 0, a, g.dir === -1)
  ctx.restore()
}

// 刃の花1つ。up=0..1 (立ち具合)、open=0..1 (両脇の開き)。床より下は切る。
function drawCluster(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, c: Cluster, floorY: number,
  up: number, open: number, a: number, timeSec: number, sink = 0,
): void {
  if (a <= 0.01 || up <= 0.005) return
  ctx.save()
  ctx.beginPath()
  ctx.rect(c.x - 140, floorY - 200, 280, 200 + 2)
  ctx.clip()
  // 両脇を先に (真ん中が手前)。
  for (const k of [0, 2, 1]) {
    const p = c.petals[k]!
    const r = HEM_ATLAS[p.name]
    if (!r) continue
    const dh = p.h
    const dw = Math.max(8, dh * (r[2] / r[3]))
    const shown = dh * up
    const baseY = floorY + (dh - shown) + sink * dh
    const shiver = Math.sin(timeSec * 31 + p.seed) * 0.02 * (1 - open)
    const rot = p.rot * (p.open ? open : 1) + shiver
    part(ctx, img, p.name, c.x, baseY, dw, dh, rot, a, hash01(p.seed) < 0.5)
  }
  ctx.restore()
}

// 床板1枚。theta=床からの起き上がりの角度 (0=倒れて床と平ら〜π/2 直立)。
//   横から見た板なので、倒れている時は厚みだけ (細い) ・起きるほど表が見える (幅を sin で開く)。
function drawBoard(ctx: CanvasRenderingContext2D, img: CanvasImageSource, b: Board, g: Geom, floorY: number, theta: number, a: number): void {
  if (a <= 0.01 || theta <= 0.02) return
  const face = 0.22 + 0.78 * Math.sin(theta)
  const rot = g.dir * (Math.PI / 2 - theta)
  part(ctx, img, b.name, b.hinge, floorY + 2, b.wid * face, b.len, rot, a, g.dir === -1)
}

/** 予告 (telegraph)。絵が無ければ false (呼び手は今の帯で描く)。 */
export function drawBladeHemTelegraph(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number, bossX: number): boolean {
  const img = hemImg
  if (!img) { preloadBladeHemAtlas(); return false }
  const g = geomOf(hz, bossX)
  traces.set(hz.id, { g, startSec: Number.NaN, activeMs: hz.activeMs })
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  const late = p >= 0.75
  const pulse = late ? 1 + 0.18 * Math.sin(timeSec * 14) : 1
  underGlow(ctx, g, floorY, (0.3 + 0.6 * p) * pulse, 30 + 26 * p)
  drawCracks(ctx, img, g, floorY, easeOut(p / 0.35), 0.5 + 0.5 * smooth01(0, 0.4, p))
  // 刃先が割れ目から覗いて震える: 40% から、踊り子の側から順に。
  const peek = smooth01(0.4, 1, p)
  if (peek > 0) {
    for (const c of g.clusters) {
      const order = smooth01(0, 0.3, peek - c.delay / 400)
      const wob = 0.5 + 0.5 * Math.sin(timeSec * 23 + c.seed * 3)
      const up = (0.07 + 0.08 * peek + 0.03 * wob * peek) * order
      drawCluster(ctx, img, c, floorY, up, 0, 0.92, timeSec)
    }
  }
  return true
}

/** 当たり (active)。絵が無ければ false。 */
export function drawBladeHemActive(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number, bossX: number): boolean {
  const img = hemImg
  if (!img) { preloadBladeHemAtlas(); return false }
  const ms = hz.elapsedMs - hz.telegraphMs
  const prev = traces.get(hz.id)
  const g = prev?.g ?? geomOf(hz, bossX)
  traces.set(hz.id, { g, startSec: timeSec - ms / 1000, activeMs: hz.activeMs })
  drawHemAt(ctx, img, g, floorY, ms, hz.activeMs, timeSec)
  return true
}

function drawHemAt(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, floorY: number,
  ms: number, activeMs: number, timeSec: number,
): void {
  const end = activeMs
  const shatterAt = end - SHATTER_LEAD_MS
  const glowA = 1 - smooth01(end - 40, end + LINGER_MS * 0.8, ms)
  underGlow(ctx, g, floorY, (ms < 120 ? 1.2 - ms / 700 : 1) * glowA, 70)
  drawCracks(ctx, img, g, floorY, 1, glowA)
  // 床板 (奥)。跳ね上がり → 揺れて止まる → 終わり際に倒れる。
  for (const b of g.boards) {
    const t = ms - b.delay
    if (t < 0) continue
    const rise = easeOutBack(t / BOARD_UP_MS)
    const fall = smooth01(shatterAt + 20, end + 140, ms)
    const bounce = fall > 0.95 ? 0 : Math.sin(fall * Math.PI) * 0.08
    const theta = (0.5 + (b.lift - 0.5) * rise) * (1 - fall) + bounce
    drawBoard(ctx, img, b, g, floorY, theta, 1 - smooth01(end + 60, end + 220, ms))
  }
  // 刃の花 (板の手前・隠れないように)。
  for (const c of g.clusters) {
    const t = ms - c.delay
    if (t < -40) continue
    const start = 0.15
    const up = start + (1 - start) * easeOutBack(t / PETAL_UP_MS)
    const open = easeOut((t - 10) / BLOOM_MS)
    const crumble = smooth01(shatterAt, shatterAt + 90, ms)
    const sink = 0.12 * smooth01(shatterAt, shatterAt + 120, ms)
    if (crumble < 1) drawCluster(ctx, img, c, floorY, Math.max(0.15, up), open, 1 - crumble, timeSec, sink)
  }
  // かけら (砕けた花びらと板から)。
  for (const c of g.clusters) drawShards(ctx, img, g, c.x, c.seed, floorY, ms - shatterAt, 'bshard', 4, 112, timeSec)
  for (const b of g.boards) {
    const bx = b.hinge + g.dir * b.len * Math.cos(b.lift) * 0.6
    drawShards(ctx, img, g, bx, b.seed, floorY, ms - (end + 40), 'wood', 2, 34, timeSec)
  }
}

// 舞うかけら (t=砕け始めからの ms)。上へはじけて落ちる。帯の ±V_BLEED の中だけ。
function drawShards(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, x0: number, seed: number, floorY: number,
  t: number, kind: 'bshard' | 'wood', n: number, height: number, timeSec: number,
): void {
  if (t <= 0 || t > LINGER_MS + 200) return
  const kinds = Math.max(1, count(kind))
  for (let k = 0; k < n; k += 1) {
    const s = seed + k * 5.77 + (kind === 'wood' ? 100 : 0)
    const born = hash01(s) * 60
    const life = 330 + hash01(s + 1) * 200
    const q = (t - born) / life
    if (q <= 0 || q >= 1) continue
    const name = `${kind}_${Math.floor(hash01(s + 2) * kinds) % kinds}`
    const r = HEM_ATLAS[name]
    if (!r) continue
    const y0 = floorY - height * (0.25 + 0.65 * hash01(s + 3))
    const vx = (hash01(s + 4) - 0.5) * 80
    const vy = 60 + hash01(s + 5) * 70        // 上へはじける高さ
    const x = Math.max(g.xStart - V_BLEED, Math.min(g.xEnd + V_BLEED, x0 + vx * q))
    const y = Math.min(floorY - 2, y0 - vy * q + 190 * q * q)
    const size = kind === 'bshard' ? 7 + hash01(s + 6) * 8 : 6 + hash01(s + 6) * 6
    const dw = size
    const dh = size * (r[3] / r[2])
    const rot = (hash01(s + 7) - 0.5) * 2 + (hash01(s + 8) - 0.5) * 9 * q + Math.sin(timeSec * 5 + s) * 0.15
    const a = (1 - smooth01(0.6, 1, q)) * smooth01(0, 0.06, q)
    part(ctx, img, name, x, y + dh / 2, dw, dh, rot, a, hash01(s + 9) < 0.5)
  }
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { g: Geom; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()

/** 当たりが消えた後の残り (かけらが落ち、板が倒れ、照りが引く)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawBladeHemLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  const img = hemImg
  for (const [id, tr] of traces) {
    if (hits.some((h) => h.id === id)) continue   // まだ帯がある = 本体側で描いている
    if (Number.isNaN(tr.startSec)) { traces.delete(id); continue }   // 予告だけで消えた (ボスが崩れた等)
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + LINGER_MS + 200) { traces.delete(id); continue }
    if (img) drawHemAt(ctx, img, tr.g, floorY, ms, tr.activeMs, timeSec)
  }
}

/** 床の技の予告 (配布版の裾払い・src/floor_warn_vis.ts) から呼ぶ: 当たりと同じ刃の花の先を、帯に並べて床から少し出す。
 *  up = 立ち具合 (0..1・花びらの高さに対する割合)・a = 濃さ。絵が無ければ false。 */
export function drawBladeHemTipsPreview(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number, bossX: number, up: number, a: number): boolean {
  const img = hemImg
  if (!img) { preloadBladeHemAtlas(); return false }
  const g = geomOf(hz, bossX)
  for (const c of g.clusters) {
    const wob = 0.5 + 0.5 * Math.sin(timeSec * 23 + c.seed * 3)
    drawCluster(ctx, img, c, floorY, up * (0.92 + 0.08 * wob), 0.35, a, timeSec)
  }
  return true
}
